/*
 * Strength phantom-variant cart guard.
 *
 * While the legacy CO "phantom" strength variants still exist, a shopper can still land one in the
 * cart: an old ?variant= link, a saved cart, a quick-add, anything that bypasses the PDP toggle.
 * snippets/strength-phantom-swaps.liquid lists those lines (rendered inside the cart drawer and cart
 * page sections). This script replaces each one with the matching real variant on the sibling
 * product, keeping quantity, subscription plan and line properties, then refreshes the cart through
 * the theme's normal cartUpdate event.
 *
 * Fails safe: if adding the real variant fails, the phantom line is left untouched (it still
 * fulfills correctly via the downstream CO -> real SKU translation). Remove this file, its
 * <script> tag in layout/theme.liquid and the snippet renders once the CO variants are deleted.
 */
(() => {
  const SELECTOR = 'script[data-strength-phantom-swaps]';
  const attempted = new Set(); // line keys already handled this page view (never retry in a loop)
  let running = false;
  let timer = null;

  const readSwaps = () => {
    const byKey = new Map();
    document.querySelectorAll(SELECTOR).forEach((node) => {
      try {
        JSON.parse(node.textContent || '[]').forEach((swap) => {
          if (swap && swap.key && swap.to && !attempted.has(swap.key)) byKey.set(swap.key, swap);
        });
      } catch (error) {
        console.warn('[strength-phantom-swap] could not read swap list', error);
      }
    });
    return [...byKey.values()];
  };

  const bundledSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } })
    );
    return [...new Set(sections)];
  };

  const run = async () => {
    if (running || !window.theme || !theme.routes || !theme.utils) return;
    const swaps = readSwaps();
    if (!swaps.length) return;

    running = true;
    swaps.forEach((swap) => attempted.add(swap.key));

    try {
      const items = swaps.map((swap) => {
        const item = { id: swap.to, quantity: swap.quantity };
        if (swap.selling_plan) item.selling_plan = swap.selling_plan;
        // Liquid serializes line properties as [[name, value], ...]; the Cart API wants an object.
        const properties = Array.isArray(swap.properties) ? Object.fromEntries(swap.properties) : swap.properties;
        if (properties && Object.keys(properties).length) item.properties = properties;
        return item;
      });

      const addResponse = await fetch(theme.routes.cart_add_url, {
        ...theme.utils.fetchConfig('json'),
        body: JSON.stringify({ items })
      });
      if (!addResponse.ok) {
        const detail = await addResponse.json().catch(() => ({}));
        console.warn('[strength-phantom-swap] add failed; leaving phantom lines as-is', detail);
        return;
      }

      const updates = {};
      swaps.forEach((swap) => { updates[swap.key] = 0; });
      const sections = bundledSections();
      const updateResponse = await fetch(theme.routes.cart_update_url, {
        ...theme.utils.fetchConfig('json'),
        body: JSON.stringify({ updates, sections })
      });
      const cart = await updateResponse.json();

      if (updateResponse.ok && cart.sections && theme.pubsub) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'strength-phantom-swap', cart });
      } else if (window.location.pathname.endsWith(theme.routes.cart_url)) {
        window.location.reload();
      }
    } catch (error) {
      console.warn('[strength-phantom-swap] swap failed; leaving phantom lines as-is', error);
    } finally {
      running = false;
    }
  };

  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(run, 250);
  };

  const start = () => {
    schedule();
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1 && (node.matches(SELECTOR) || node.querySelector(SELECTOR))) {
            schedule();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
